import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { COMMAND_PRIORITY_EDITOR } from 'lexical';
import { FC, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { createTemplateLinkCheck, type TemplateLinkTarget } from '../../../lib';
import {
  findTemplateLinkLines,
  findTemplateLinkOption,
  readTemplateLinkTarget,
  START_TEMPLATE_LINK_COMMAND,
  type TemplateLinkAdapter,
} from '../templateLinkMode';

import styles from './TemplateLinkModePlugin.module.scss';

const LINK_MODE = 'data-link-mode';
const LINK_SOURCE = 'data-link-source';
const LINK_TARGET = 'data-link-target';

const BAR_WIDTH = 3;
// The bar stands this far left of its lines' boxes, off their text.
const BAR_OFFSET = 6;
// Selected lines this close read as one block with one bar: the gap between
// two lines of a section.
const BAR_JOIN_GAP = 16;
const CURVE_MIN_REACH = 48;

type Point = { x: number; y: number };
type Bar = { x: number; top: number; bottom: number };
type Overlay = { bars: Bar[]; pointer: Point | null };

type LinkMode = {
  lineIds: string[];
  replaced?: TemplateLinkTarget;
  canLink: (target: TemplateLinkTarget) => boolean;
};

const EMPTY_OVERLAY: Overlay = { bars: [], pointer: null };

const measureBars = (lines: HTMLElement[]) =>
  lines
    .map((line) => line.getBoundingClientRect())
    .sort((first, second) => first.top - second.top)
    .reduce<Bar[]>((bars, rect) => {
      const last = bars[bars.length - 1];
      return last && rect.top - last.bottom <= BAR_JOIN_GAP
        ? [
            ...bars.slice(0, -1),
            {
              x: Math.min(last.x, rect.left),
              top: last.top,
              bottom: Math.max(last.bottom, rect.bottom),
            },
          ]
        : [...bars, { x: rect.left, top: rect.top, bottom: rect.bottom }];
    }, []);

const placeBar = (bar: Bar) => ({
  x: bar.x - BAR_OFFSET - BAR_WIDTH / 2,
  y: bar.top,
  height: bar.bottom - bar.top,
});

// The curve leaves the bar nearest the pointer at its middle.
const findCurveStart = (bars: Bar[], pointer: Point): Point | null => {
  const nearest = bars.reduce<Bar | null>(
    (best, bar) =>
      best &&
      Math.abs((best.top + best.bottom) / 2 - pointer.y) <=
        Math.abs((bar.top + bar.bottom) / 2 - pointer.y)
        ? best
        : bar,
    null,
  );
  return nearest
    ? { x: nearest.x - BAR_OFFSET, y: (nearest.top + nearest.bottom) / 2 }
    : null;
};

// Out of the bar to the left and into the pointer from the left, so the
// arrowhead always points at what is under the pointer.
const drawCurve = (from: Point, to: Point) => {
  const reach = Math.max(CURVE_MIN_REACH, Math.abs(to.y - from.y) / 3);
  return `M ${from.x} ${from.y} C ${from.x - reach} ${from.y}, ${to.x - reach} ${to.y}, ${to.x} ${to.y}`;
};

// Link mode: the lines to link stand out with a bar on their left, a curve
// runs from it to the pointer, and a click on an option the lines may hang on
// adds it to their links, or puts it in place of the one being replaced.
// Escape or a click elsewhere leaves the lines as they were.
export const TemplateLinkModePlugin: FC<{ adapter: TemplateLinkAdapter }> = ({
  adapter,
}) => {
  const [editor] = useLexicalComposerContext();
  const markerId = `template-link-arrow-${useId().replace(/[^\w-]/g, '')}`;

  const [mode, setMode] = useState<LinkMode | null>(null);
  const [overlay, setOverlay] = useState<Overlay>(EMPTY_OVERLAY);

  // A consumer may build the adapter anew on every render; the mode's
  // listeners read the latest one and are not set up again for it.
  const latestAdapter = useRef(adapter);
  useEffect(() => {
    latestAdapter.current = adapter;
  });

  useEffect(
    () =>
      editor.registerCommand(
        START_TEMPLATE_LINK_COMMAND,
        (start) => {
          if (!editor.isEditable()) {
            return false;
          }
          const linked =
            start?.lineIds ?? latestAdapter.current.readSelectedLineIds();
          if (!linked.length) {
            return false;
          }
          setOverlay(EMPTY_OVERLAY);
          setMode({
            lineIds: linked,
            replaced: start?.replaced,
            canLink: createTemplateLinkCheck({
              graph: latestAdapter.current.readLinkGraph(),
              lineIds: linked,
            }),
          });
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor],
  );

  useEffect(() => {
    const root = editor.getRootElement();
    if (!mode || !root) {
      return undefined;
    }
    const pointer: { at: Point | null } = { at: null };
    const hover: { option: HTMLElement | null } = { option: null };

    const findOption = (element: EventTarget | null) =>
      findTemplateLinkOption(root, element);

    const markHover = (option: HTMLElement | null) => {
      if (hover.option === option) {
        return;
      }
      hover.option?.removeAttribute(LINK_TARGET);
      hover.option = option;
      option?.setAttribute(
        LINK_TARGET,
        mode.canLink(readTemplateLinkTarget(option)) ? 'allowed' : 'refused',
      );
    };

    // Lines the editor draws anew lose their mark, so every frame marks them.
    const draw = () => {
      const lines = findTemplateLinkLines(root, mode.lineIds);
      lines.forEach((line) => line.setAttribute(LINK_SOURCE, ''));
      setOverlay({ bars: measureBars(lines), pointer: pointer.at });
    };

    // The document may have changed since the mode began: the link is
    // checked again against it.
    const link = (target: TemplateLinkTarget) =>
      editor.update(() => {
        const check = createTemplateLinkCheck({
          graph: latestAdapter.current.readLinkGraph(),
          lineIds: mode.lineIds,
        });
        if (check(target)) {
          latestAdapter.current.changeLinesOpeners(mode.lineIds, {
            added: target,
            removed: mode.replaced,
          });
        }
      });

    const onMouseMove = (event: MouseEvent) => {
      pointer.at = { x: event.clientX, y: event.clientY };
      markHover(findOption(event.target));
      draw();
    };

    const onScroll = () => {
      if (pointer.at) {
        markHover(
          findOption(
            document.elementFromPoint?.(pointer.at.x, pointer.at.y) ?? null,
          ),
        );
      }
      draw();
    };

    // A press on an option keeps the caret and the option's own handlers out
    // of it; a press anywhere else ends the mode and does what it does.
    const onMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) {
        return;
      }
      if (!findOption(event.target)) {
        setMode(null);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    };

    // An option the lines may not hang on is refused, and the mode goes on.
    const onClick = (event: MouseEvent) => {
      const option = findOption(event.target);
      if (!option) {
        setMode(null);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const target = readTemplateLinkTarget(option);
      if (mode.canLink(target)) {
        link(target);
        setMode(null);
      }
    };

    // Ahead of the pop-ups and modals the editor sits in, which close on
    // Escape too.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setMode(null);
      }
    };

    root.setAttribute(LINK_MODE, '');
    draw();
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('click', onClick, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('mousemove', onMouseMove, true);
      window.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('click', onClick, true);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      root.removeAttribute(LINK_MODE);
      root
        .querySelectorAll(`[${LINK_SOURCE}]`)
        .forEach((line) => line.removeAttribute(LINK_SOURCE));
      hover.option?.removeAttribute(LINK_TARGET);
    };
  }, [editor, mode]);

  if (!mode) {
    return null;
  }
  const { bars, pointer } = overlay;
  const start = pointer && findCurveStart(bars, pointer);

  return createPortal(
    <svg
      className={styles.overlay}
      aria-hidden
      data-component="template-link-overlay"
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="10"
          refY="5"
          markerWidth="10"
          markerHeight="10"
          markerUnits="userSpaceOnUse"
          orient="auto"
        >
          <path d="M 0 0 L 10 5 L 0 10 Z" className={styles.arrow} />
        </marker>
      </defs>
      {bars.map((bar) => {
        const { x, y, height } = placeBar(bar);
        return (
          <rect
            key={`${bar.top}:${bar.bottom}`}
            className={styles.bar}
            x={x}
            y={y}
            width={BAR_WIDTH}
            height={height}
            rx={BAR_WIDTH / 2}
            data-component="template-link-bar"
          />
        );
      })}
      {start && pointer && (
        <path
          className={styles.curve}
          d={drawCurve(start, pointer)}
          markerEnd={`url(#${markerId})`}
          data-component="template-link-curve"
        />
      )}
    </svg>,
    document.body,
  );
};
